import React from "react";
import {
   Accordion,
   AccordionSummary,
   AccordionDetails,
   Typography,
   Button,
   Box,
   IconButton,
   Divider, Autocomplete, TextField
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AddOutlined, RemoveCircleOutline } from "@mui/icons-material";
import { api } from "../../services/api";
import { isValidAlbRedirectUrl } from "../CreateResource/utils/formHelpers";

// Import your global UI components
import { FormInput, FormDropdown, FormRadio, FormTextArea, FormCheckbox } from '../../components/ui';

const ListenersAlbAccordion = React.forwardRef(({
   field,
   formConfig,
   formData,
   setFormData,
   dropdownData = {},
   activeAccountId,
   resourceId,
   formSubmitted
}, ref) => {
   const items = Array.isArray(formData[field.fieldName]?.value)
      ? formData[field.fieldName].value
      : [];

   const [expandedIndex, setExpandedIndex] = React.useState(0);
   const [validationErrors, setValidationErrors] = React.useState({});
   const collectionFields = field.collectionFields || [];
   const [nestedDropdownData, setNestedDropdownData] = React.useState({});
   const [globalApiDropdownData, setGlobalApiDropdownData] = React.useState({});
   const [showValidationErrors, setShowValidationErrors] = React.useState(false);
   const globalFetchCacheRef = React.useRef({});

   const protocolDependency = items
      .map(item => item?.Protocol?.value || "")
      .join("|");

   React.useEffect(() => {
      items.forEach((item, listenerIdx) => {
         (collectionFields || []).forEach((cf) => {
            if (cf.fieldType === "collection" && cf.fieldName === "TargetGroupCollection") {
               (cf.collectionFields || []).forEach((nf) => {
                  if (nf.dropdownType === "api" && nf.fieldName === "TargetGroup") {
                     const location = formData?.location?.value;
                     const vpcId = formData?.vpcId?.value;
                     const protocol = item?.Protocol?.value;

                     if (location && vpcId && protocol) {
                        fetchNestedDropdownOptions(nf, listenerIdx, item);
                     }
                  }
               });
            }
         });
      });
   }, [protocolDependency, formData?.location?.value, formData?.vpcId?.value, activeAccountId, resourceId]);





   // ============================================================================
   // FIX: Explicitly sort inner sub-tabs so that Secure Settings and WAF are 
   // forced to the bottom of the Accordion, and Target Groups/General stay at the top.
   // ============================================================================
   const innerSubTabs = [...new Set((collectionFields || []).map(f => f?.subTab || "General"))].sort((a, b) => {
      const aName = a.toLowerCase();
      const bName = b.toLowerCase();
      const getRank = (name) => {
         if (name.includes("secure") || name.includes("security")) return 2;
         if (name.includes("waf") || name.includes("firewall") || name.includes("web application")) return 3;
         return 1; // General, Target Groups, etc. render first (top)
      };
      return getRank(aName) - getRank(bName);
   });

   //   const extraRootFields = (formConfig || []).filter(f => f.tabName === field.tabName && f.fieldName !== field.fieldName);
   const extraRootFields = React.useMemo(
      () => (formConfig || []).filter(
         f => f.tabName === field.tabName && f.fieldName !== field.fieldName
      ),
      [formConfig, field.tabName, field.fieldName]
   );


   

   const globalSubTabs = React.useMemo(
      () =>
         [...new Set((extraRootFields || []).map(f => f?.subTab).filter(Boolean))].sort((a, b) => {
            const aName = a.toLowerCase();
            const bName = b.toLowerCase();
            const getRank = (name) => {
               if (name.includes("secure") || name.includes("security")) return 1;
               if (name.includes("waf") || name.includes("firewall") || name.includes("web application")) return 2;
               return 3;
            };
            return getRank(aName) - getRank(bName);
         }),
      [extraRootFields]
   );

   // ============================================================================
   // HTTPS DEPENDENCY LOGIC (Controls Secure Settings Tab)
   // ============================================================================
   const checkIsHttps = (item) => {
      const pVal = item?.Protocol?.value || "";
      return String(pVal).toUpperCase().trim() === "HTTPS";
   };
   const hasHttpsListener = (items || []).some(checkIsHttps);

   const globalContextData = {
      ...formData,
      Protocol: hasHttpsListener ? { value: "HTTPS", label: "HTTPS" } : { value: "HTTP", label: "HTTP" }
   };


   React.useEffect(() => {
      const region = formData?.location?.value;
      const accountId = activeAccountId || formData?.ActiveAccountID?.value;

      if (!hasHttpsListener) return;
      if (!region || !accountId) return;

      (extraRootFields || []).forEach((f) => {
         if (f.dropdownType !== "api") return;

         if (f.fieldName === "SecurityCategory" || f.fieldName === "PolicyName") {
            fetchGlobalDropdownOptions(f);
            return;
         }

         //  if (f.fieldName === "CertificateFromACM") {
         //    const certSource =
         //      formData?.CertificateSource?.value ||
         //      globalContextData?.CertificateSource?.value;

         //    if (certSource === "FromACM") {
         //      fetchGlobalDropdownOptions(f);
         //    }
         //    return;
         //  }

         if (f.fieldName === "CertificateFromACM") {
            const certSource =
               formData?.CertificateSource?.value ||
               globalContextData?.CertificateSource?.value ||
               "FromACM";

            if (certSource === "FromACM") {
               fetchGlobalDropdownOptions(f);
            }
            return;
         }


         if (f.fieldName === "WAFWebACL") {
            const wafEnabled =
               formData?.WAFEnable?.value === true ||
               String(formData?.WAFEnable?.value).toLowerCase() === "true";

            if (wafEnabled) {
               fetchGlobalDropdownOptions(f);
            }
         }
      });
   }, [
      extraRootFields,
      hasHttpsListener,
      formData?.location?.value,
      activeAccountId,
      formData?.ActiveAccountID?.value,
      formData?.CertificateSource?.value,
      formData?.WAFEnable?.value
   ]);





   const isFieldEmpty = (value) => {
      return (
         value === null ||
         value === undefined ||
         value === "" ||
         (Array.isArray(value) && value.length === 0)
      );
   };

   const validateField = (fieldName, fieldValue, isRequired, fieldConfig = {}) => {
      if (isRequired && isFieldEmpty(fieldValue)) {
         return `${fieldName} is required`;
      }



      //  RESPONSE BODY MAX LENGTH VALIDATION
      if (fieldConfig.fieldName === "ResponseBody" && fieldValue) {
         if (fieldValue.length > 1024) {
            return "Maximum 1024 characters allowed";
         }
      }



      if (fieldConfig.fieldName === "RedirectFullURL" && !isFieldEmpty(fieldValue)) {
         if (!isValidAlbRedirectUrl(fieldValue)) {
            return "Enter a valid URL (e.g., https://example.com/path)";
         }
      }

      if (fieldConfig.fieldName === "ResponseCode" && !isFieldEmpty(fieldValue)) {
         const num = Number(fieldValue);
         if (Number.isNaN(num) || num < 200 || num > 599) {
            return `${fieldName} must be between 200 and 599`;
         }
      }

    

      if (fieldConfig.fieldName === "ContentType") {
         if (isFieldEmpty(fieldValue)) {
            return "Content Type is required";
         }

         const contentTypePattern = /^[a-z]+\/[a-z0-9.+-]+$/i;

         if (!contentTypePattern.test(fieldValue)) {
            return "Enter valid content type (e.g., text/plain)";
         }
      }





      if (fieldConfig.fieldName === "Weight" && !isFieldEmpty(fieldValue)) {
         const num = Number(fieldValue);
         if (Number.isNaN(num) || num < 0 || num > 999) {
            return `${fieldName} must be between 0 and 999`;
         }
      }

      if (fieldConfig.fieldName === "Port" && !isFieldEmpty(fieldValue)) {
         const num = Number(fieldValue);
         if (Number.isNaN(num) || num < 1 || num > 65535) {
            return `${fieldName} must be between 1 and 65535`;
         }
      }

      if (fieldConfig.fieldName === "TargetGroupStickinessDuration" && !isFieldEmpty(fieldValue)) {
         const num = Number(fieldValue);

         if (Number.isNaN(num) || num < 1 || num > 604800) {
            return `${fieldName} must be between 1 and 604800 seconds`;
         }
      }



      return null;
   };

   const validateItemFields = (item, fields, path = "", listenerContext = null) => {
      const errors = {};

      (fields || []).forEach((f) => {
         // if (!shouldShowField(f, item, listenerContext)) return;


         const isForceValidate =
            f.isRequired ||
            f.fieldName === "TargetGroup";


         const shouldValidate =
            shouldShowField(f, item, listenerContext) || isForceValidate;

         if (!shouldValidate) return;



         const currentPath = path ? `${path}_${f.fieldName}` : f.fieldName;
         const type = f.fieldType?.toLowerCase().trim() || "";

         if (type === "collection") {


            if (
               f.fieldName === "TargetGroupCollection" &&
               item?.RoutingActions?.value === "ForwardToTargetGroups"
            ) {
               const rows = item[f.fieldName]?.value || [];

               rows.forEach((row, rowIdx) => {
                  //  HANDLE MISSING FIELD (your case)
                  if (!row || !row.TargetGroup || !row.TargetGroup.value) {
                     errors[`${currentPath}_${rowIdx}_TargetGroup`] =
                        "Target group is required";
                  }
               });
            }

            const rows = Array.isArray(item?.[f.fieldName]?.value)
               ? item[f.fieldName].value
               : [];

            if (f.isRequired && rows.length === 0) {
               errors[currentPath] = `${f.fieldLabel || f.fieldName} is required`;
            }

            rows.forEach((row, rowIdx) => {
               const nestedErrors = validateItemFields(
                  row,
                  f.collectionFields || [],
                  `${currentPath}_${rowIdx}`,
                  item
               );
               Object.assign(errors, nestedErrors);
            });
         } else {
            const value = item?.[f.fieldName]?.value;
            const error = validateField(
               f.fieldLabel || f.fieldName,
               value,
               f.isRequired,
               f
            );
            if (error) {
               errors[currentPath] = error;
            }
         }
      });

      return errors;
   };



   const validateAllListenersFields = () => {
      const allErrors = {};
      const portMap = {};

      items.forEach((item, idx) => {
         const itemErrors = validateItemFields(item, collectionFields);

         const portVal = item?.Port?.value;
         if (portVal) {
            if (!portMap[portVal]) {
               portMap[portVal] = [];
            }
            portMap[portVal].push(idx);
         }

         Object.keys(itemErrors).forEach((fieldPath) => {
            allErrors[`listener_${idx}_${fieldPath}`] = itemErrors[fieldPath];
         });
      });

      //  ONLY mark duplicates AFTER first occurrence
      Object.entries(portMap).forEach(([port, indices]) => {
         if (indices.length > 1) {
            indices.slice(1).forEach((i) => {
               allErrors[`listener_${i}_Port`] = "Listener with same protocol and port already exists";
            });
         }
      });

      return allErrors;
   };


   const validateGlobalFields = () => {
      const errors = {};

      (extraRootFields || []).forEach((f) => {
         if (!shouldShowField(f, globalContextData)) return;


         // IMPORTANT: Only validate secure tab when HTTPS exists
         const isSecureField = f.subTab?.toLowerCase().includes("secure");

         // if (isSecureField && !hasHttpsListener) {
         //    return; // don't validate if no HTTPS listener
         // }


         //  If secure field → ONLY depend on HTTPS check
         if (isSecureField) {
            if (!hasHttpsListener) return;
         }
         else {
            //  Only apply dependency logic for NON-secure fields
            if (!shouldShowField(f, globalContextData)) return;
         }



         // const value = formData?.[f.fieldName]?.value;
         const value = globalContextData?.[f.fieldName]?.value;
         const error = validateField(
            f.fieldLabel || f.fieldName,
            value,
            f.isRequired,
            f
         );

         if (error) {
            errors[`global_${f.fieldName}`] = error;
         }
      });

      return errors;
   };




   const validateAll = () => {
      const listenerErrors = validateAllListenersFields();
      const globalErrors = validateGlobalFields();

      const collectionError =
         field?.isRequired && (!Array.isArray(items) || items.length === 0)
            ? { [field.fieldName]: `${field.fieldLabel || field.fieldName} is required` }
            : {};

      const allErrors = {
         ...collectionError,
         ...listenerErrors,
         ...globalErrors,
      };

      setValidationErrors(allErrors);
      return Object.keys(allErrors).length === 0;
   };


   React.useEffect(() => {
      if (formSubmitted || showValidationErrors) {
         validateAll();
      }
   }, [formData, formSubmitted, showValidationErrors]);

   React.useImperativeHandle(ref, () => ({
      validate: () => {
         setShowValidationErrors(true);
         return validateAll();
      }
   }));

   React.useEffect(() => {
      if (!items || items.length === 0) {
         addListener();
      }

   }, []);







   const getDefaultValue = (child) => {
      const type = child.fieldType?.toLowerCase().trim() || "";



      if (child.fieldName === "CertificateSource") {
         return "FromACM";
      }



      if (child.fieldName === "Protocol") {
         return "HTTPS";
      }


     
      if (type === "dropdown") {
         if (child.dropdownValues) {
            try {
               const parsed = JSON.parse(child.dropdownValues);
               const def = parsed.find(opt => opt.isDefault);
               if (def) return def.value;

               return parsed[0]?.value || "";
            } catch { }
         }
      }


      if (type === "radio button") {
         if (child.dropdownValues) {
            try {
               const parsed = JSON.parse(child.dropdownValues);
               const defaultOption = parsed.find(opt => opt.isDefault);
               if (defaultOption) return defaultOption.value;
            } catch { }
         }
         return child.dropdownSource?.split(",")[0]?.trim() || "";
      }


      if (type === "checkbox") {
         return false;
      }

      if (type === "collection") {
         // For TargetGroupCollection, create one default row like NLB
         if (child.fieldName === "TargetGroupCollection") {
            return [buildDefaultItem(child.collectionFields || [])];
         }
         return [];
      }

      return child.defaultValue || "";
   };

   const buildDefaultItem = (fields) => {
      const item = {};
      fields.forEach((f) => {
         const defaultVal = getDefaultValue(f);
         item[f.fieldName] = {
            value: defaultVal,
            label: Array.isArray(defaultVal) ? `${defaultVal.length} items` : defaultVal
         };
      });
      return item;
   };




   const addListener = () => {
      const next = [...items, buildDefaultItem(collectionFields)];
      setFormData(prev => ({ ...prev, [field.fieldName]: { value: next, label: `${next.length} listeners` } }));
      setExpandedIndex(next.length - 1);

   };

   const removeListener = (idx) => {
      const next = items.filter((_, i) => i !== idx);
      setFormData(prev => ({ ...prev, [field.fieldName]: { value: next, label: `${next.length} listeners` } }));
      setExpandedIndex(Math.max(0, idx - 1));

   };






   const updateChild = (listenerIdx, childName, value, label) => {
      const next = items.map((it, i) =>
         i !== listenerIdx ? it : { ...it, [childName]: { value, label: label ?? value } }
      );


      if (childName === "RoutingActions") {

         const tgField = collectionFields.find(
            f => f.fieldName === "TargetGroupCollection"
         );

         if (value === "ForwardToTargetGroups") {
            next[listenerIdx] = {
               ...next[listenerIdx],
               TargetGroupCollection: tgField
                  ? {
                     value: [buildDefaultItem(tgField.collectionFields || [])],
                     label: "1 items"
                  }
                  : undefined,
               RedirectFullURL: undefined,
               RedirectStatusCode: undefined,
               ResponseCode: undefined,
               ContentType: undefined,
               ResponseBody: undefined
            };
         }

         if (value === "RedirectToURL") {
            next[listenerIdx] = {
               ...next[listenerIdx],
               RedirectFullURL: { value: "", label: "" },
               RedirectStatusCode: { value: "", label: "" },
               TargetGroupCollection: undefined,
               ResponseCode: undefined,
               ContentType: undefined,
               ResponseBody: undefined
            };
         }

         if (value === "ReturnFixedResponse") {


            const contentTypeField = collectionFields.find(
               f => f.fieldName === "ContentType"
            );

            const defaultContentType =
               contentTypeField?.defaultValue || "text/plain";

            next[listenerIdx] = {
               ...next[listenerIdx],
               ResponseCode: { value: "", label: "" },
               ContentType: { value: defaultContentType, label: defaultContentType },
               ResponseBody: { value: "", label: "" },
               TargetGroupCollection: undefined,
               RedirectFullURL: undefined,
               RedirectStatusCode: undefined
            };
         }
      }

      setFormData(prev => ({
         ...prev,
         [field.fieldName]: { value: next, label: `${next.length} listeners` }
      }));

      setValidationErrors(prev => {
         let nextErrors = { ...prev };

         //  Normal validation
         const fieldConfig = collectionFields.find(f => f.fieldName === childName);
         if (fieldConfig) {
            const error = validateField(
               fieldConfig.fieldLabel || fieldConfig.fieldName,
               value,
               fieldConfig.isRequired,
               fieldConfig
            );

            const key = `listener_${listenerIdx}_${childName}`;

            if (!error) delete nextErrors[key];
            else nextErrors[key] = error;
         }

         //  DUPLICATE PORT CHECK (ONLY AFTER FIRST)
         if (childName === "Port") {
            const portMap = {};

            next.forEach((it, idx) => {
               const p = it?.Port?.value;
               if (p) {
                  if (!portMap[p]) portMap[p] = [];
                  portMap[p].push(idx);
               }
            });

            // Clear old duplicate errors
            Object.keys(nextErrors).forEach((key) => {
               if (key.includes("_Port") && nextErrors[key] === "Listener with same protocol and port already exists") {
                  delete nextErrors[key];
               }
            });

            //  Apply only to duplicates after first
            Object.entries(portMap).forEach(([port, indices]) => {
               if (indices.length > 1) {
                  indices.slice(1).forEach((i) => {
                     nextErrors[`listener_${i}_Port`] = "Listener with same protocol and port already exists";
                  });
               }
            });
         }

         return nextErrors;
      });
   };






   const updateGlobalField = (fieldName, value, label) => {
      setFormData(prev => ({
         ...prev,
         [fieldName]: { value, label: label ?? value }
      }));

      // immediately clear current field error if value is valid
      setValidationErrors(prev => {
         const next = { ...prev };
         const fieldConfig = extraRootFields.find(f => f.fieldName === fieldName);

         if (fieldConfig) {
            const error = validateField(
               fieldConfig.fieldLabel || fieldConfig.fieldName,
               value,
               fieldConfig.isRequired,
               fieldConfig
            );

            if (!error) {
               delete next[`global_${fieldName}`];
            } else {
               next[`global_${fieldName}`] = error;
            }
         }

         return next;
      });
   };


   const updateNestedChild = (listenerIdx, collectionName, nestedIdx, fieldName, value, label) => {
      const currentListener = items[listenerIdx];
      const collectionArray = Array.isArray(currentListener?.[collectionName]?.value) ? [...currentListener[collectionName].value] : [];
      if (!collectionArray[nestedIdx]) collectionArray[nestedIdx] = {};
      collectionArray[nestedIdx] = { ...collectionArray[nestedIdx], [fieldName]: { value, label: label ?? value } };
      updateChild(listenerIdx, collectionName, collectionArray, `${collectionArray.length} items`);
   };

   const addNestedRow = (listenerIdx, collectionName, nestedFields) => {
      const currentListener = items[listenerIdx];
      const collectionArray = Array.isArray(currentListener?.[collectionName]?.value) ? [...currentListener[collectionName].value] : [];
      collectionArray.push(buildDefaultItem(nestedFields));
      updateChild(listenerIdx, collectionName, collectionArray, `${collectionArray.length} items`);
   };

   const removeNestedRow = (listenerIdx, collectionName, nestedIdx) => {
      const currentListener = items[listenerIdx];
      const collectionArray = Array.isArray(currentListener?.[collectionName]?.value) ? [...currentListener[collectionName].value] : [];
      collectionArray.splice(nestedIdx, 1);
      updateChild(listenerIdx, collectionName, collectionArray, `${collectionArray.length} items`);
   };

   const shouldShowField = (f, contextData, listenerContext = null) => {
      if (f.fieldName === "TargetGroup" || f.fieldName === "TargetGroupArn") return true;

      if (!f.dependentOn || String(f.dependentOn).trim() === "") return true;

      const depFields = String(f.dependentOn).split(",").map(s => s.trim()).filter(Boolean);

      const getActualVal = (depField) => {
         if (contextData?.[depField]?.value !== undefined) return contextData[depField].value;

         if (listenerContext?.[depField]?.value !== undefined) return listenerContext[depField].value;

         if (formData?.[depField]?.value !== undefined) return formData[depField].value;

         if (globalContextData?.[depField]?.value !== undefined) return globalContextData[depField].value;

         return undefined;
      };

      if (f.dependentValue == null || String(f.dependentValue).trim() === "") {
         return depFields.every((depField) => {
            const actualVal = getActualVal(depField);
            if (actualVal === undefined || actualVal === null || actualVal === "") return false;
            if (actualVal === false || String(actualVal).toLowerCase() === "false") return false;

            return true;
         });
      }

      const expectedValues = String(f.dependentValue)
         .split(",")
         .map(s => s.trim().toLowerCase());

      return depFields.every((depField) => {
         const actualVal = getActualVal(depField);
         if (actualVal === undefined || actualVal === null) return false;

         return expectedValues.includes(String(actualVal).toLowerCase());
      });
   };


   const calculatePercent = (listenerIdx, collectionName, nestedIdx) => {
      const rows = Array.isArray(items[listenerIdx]?.[collectionName]?.value) ? items[listenerIdx][collectionName].value : [];
      const totalWeight = rows.reduce((sum, r) => sum + Number(r.Weight?.value || 0), 0);
      if (totalWeight === 0) return "0%";
      const weight = Number(rows[nestedIdx]?.Weight?.value || 0);
      return `${Math.round((weight / totalWeight) * 100)}%`;
   };

   const shouldFieldBeFullRow = (field) => {
      const name = field.fieldName || "";
      const type = field.fieldType?.toLowerCase().trim() || "";

      if (type === "textarea") return true;
      if (name.includes("Routing action") || name.includes("Pre-routing action") || name.includes("RoutingActions")) return true;
      if (name.includes("CertificateFromACM") || name === "Certificate (from ACM)") return true;

      if (name === "WAFEnable") {
         return true;
      } if (name === "SecurityCategory" || name === "PolicyName" || name === "CertificateSource") return false;

      if (type === "checkbox") return true;

      if (field.layout === "Horizontal") return false;
      if (field.fullWidth === false) return false;
      if (type === "radio button") return true;

      return false;
   };

   const normalizeOptions = (source) => {
      if (!Array.isArray(source)) return [];
      return source
         .filter(opt => opt != null)
         .map(opt => {
            if (typeof opt === 'object') {
               return {
                  ...opt,
                  value: opt.value !== undefined ? opt.value : (opt.id || ""),
                  label: opt.label || opt.text || opt.value || opt.name || ""
               };
            }
            return { value: String(opt).trim(), label: String(opt).trim() };
         });
   };

   const buildNestedApiUrl = (f, listenerItem) => {
      if (!f?.dropdownSource) return null;

      let url = f.dropdownSource;

      const location = formData?.location?.value || "";
      const vpcId = formData?.vpcId?.value || "";
      const protocol = listenerItem?.Protocol?.value || "";
      const accountId = activeAccountId || formData?.ActiveAccountID?.value || "";

      //  FIX 2: Add a fallback so the URL doesn't break if resourceId is missing on load
      const resourceTypeId = resourceId || "10";

      url = url.replace("{location}", encodeURIComponent(location));
      url = url.replace("{vpcId}", encodeURIComponent(vpcId));
      url = url.replace("{activeAccountId}", encodeURIComponent(accountId));
      url = url.replace("{ActiveAccountID}", encodeURIComponent(accountId));
      url = url.replace("{protocol}", encodeURIComponent(protocol.toLowerCase()));
      url = url.replace("{resourceTypeId}", encodeURIComponent(resourceTypeId));

      return url;
   };


   const buildGlobalApiUrl = (f) => {
      if (!f?.dropdownSource) return null;

      let url = f.dropdownSource;

      const region = formData?.location?.value || "";
      const accountId = activeAccountId || formData?.ActiveAccountID?.value || "";
      const certificateSource =
         formData?.CertificateSource?.value ||
         globalContextData?.CertificateSource?.value ||
         "";

      url = url.replace("{location}", encodeURIComponent(region));
      url = url.replace("{region}", encodeURIComponent(region));
      url = url.replace("{activeAccountId}", encodeURIComponent(accountId));
      url = url.replace("{ActiveAccountID}", encodeURIComponent(accountId));
      url = url.replace("{CertificateSource}", encodeURIComponent(certificateSource));

      const params = new URLSearchParams();

      if (f.fieldName === "SecurityCategory" || f.fieldName === "PolicyName") {
         if (!region || !accountId) return null;
         params.append("region", region);
         params.append("activeAccountId", accountId);
      }

      // IMPORTANT: don't append region again if already in path
      //   if (f.fieldName === "CertificateFromACM") {
      //     if (!region) return null;
      //     // no extra query param unless backend explicitly needs it
      //   }

      if (f.fieldName === "CertificateFromACM") {
         if (!region || !accountId) return null;
         params.append("region", region);
         params.append("activeAccountId", accountId);
      }


      if (f.fieldName === "WAFWebACL") {
         if (!region || !accountId) return null;
         params.append("region", region);
         params.append("activeAccountId", accountId);
      }

      const query = params.toString();
      if (query) {
         url += (url.includes("?") ? "&" : "?") + query;
      }

      return url;
   };




   const fetchGlobalDropdownOptions = async (f) => {
      if (f.dropdownType !== "api") return;

      const url = buildGlobalApiUrl(f);

      if (!url || url.includes("undefined")) return;

      const cacheKey = `${f.fieldName}::${url}`;
      if (globalFetchCacheRef.current[cacheKey]) return;

      globalFetchCacheRef.current[cacheKey] = true;

      try {
         const response = await api.get(url, {
            headers: {
               "X-CORRELATION-ID": crypto.randomUUID()
            }
         });

         const apiData = Array.isArray(response.data)
            ? response.data
            : Array.isArray(response.data?.data)
               ? response.data.data
               : Array.isArray(response.data?.result)
                  ? response.data.result
                  : [];

         const options = apiData.map(item => ({
            value: item[f.valueField] ?? item.value,
            label: item[f.labelField] ?? item.label ?? item.text ?? item.value
         }));

         setGlobalApiDropdownData(prev => ({
            ...prev,
            [f.fieldName]: options
         }));
      } catch (error) {
         console.error(`Error fetching global dropdown for ${f.fieldName}:`, error, url);
         setGlobalApiDropdownData(prev => ({
            ...prev,
            [f.fieldName]: []
         }));
      }
   };

   const fetchNestedDropdownOptions = async (f, listenerIdx, listenerItem) => {
      if (f.dropdownType !== "api") return;

      const key = `${listenerIdx}_${f.fieldName}`;
      const url = buildNestedApiUrl(f, listenerItem);

      if (!url || url.includes("undefined") || url.includes("//")) return;

      try {
         const region = formData?.location?.value || "";
         const accountId = activeAccountId || formData?.ActiveAccountID?.value || "";

         const response = await api.get(url, {
            params: {
               region: region,
               activeAccountId: accountId
            },
            headers: {
               "X-CORRELATION-ID": crypto.randomUUID()
            }
         });

         const apiData = Array.isArray(response.data)
            ? response.data
            : Array.isArray(response.data?.data)
               ? response.data.data
               : Array.isArray(response.data?.result)
                  ? response.data.result
                  : [];

         const options = apiData.map(item => ({
            value: item[f.valueField] ?? item.value,
            label: item[f.labelField] ?? item.label ?? item.text ?? item.value
         }));

         setNestedDropdownData(prev => ({
            ...prev,
            [key]: options
         }));
      } catch (error) {
         console.error(`Error fetching nested dropdown for ${f.fieldName}:`, error);
         setNestedDropdownData(prev => ({
            ...prev,
            [key]: []
         }));
      }
   };

   const renderGenericField = (
      f,
      contextData,
      listenerContext,
      onChangeCallback,
      hideLabel = false,
      listenerIdx = null,
      customErrorKey = null
   ) => {
      if (!shouldShowField(f, contextData, listenerContext)) return null;

      const value = contextData?.[f.fieldName]?.value ?? "";
      const type = f.fieldType?.toLowerCase().trim() || "";



      const errorKey =
         customErrorKey ||
         (listenerIdx !== null
            ? `listener_${listenerIdx}_${f.fieldName}`
            : `global_${f.fieldName}`);

      const error = validationErrors[errorKey];

      const isErrorVisible = (f.fieldName === "Port" && !!error) || !!error && (showValidationErrors || formSubmitted);
      const helperText = isErrorVisible ? error : "";

   
      if (type === "text") {
         const isPortField = f.fieldName === "Port";
         const isWeightField = f.fieldName === "Weight";
         const isDurationField = f.fieldName === "TargetGroupStickinessDuration";

         return (
            <Box sx={{ width: "100%", mt: 1 }}>
               <FormInput
                  name={f.fieldName}
                  label={hideLabel ? "" : f.fieldLabel}
                  value={value}
                  disabled={f.isEnabled === false}
                  size="medium"
                  required={f.isRequired}
                  error={isErrorVisible}
                  helperText={
                     isErrorVisible
                        ? error
                        : isPortField
                           ? "1-65535"
                           : isWeightField
                              ? "0-999"
                              : isDurationField
                                 ? "1-604800 seconds"
                                 : f.fieldName === "RedirectFullURL"
                                    ? "https://example.com/path"
                                    : f.fieldName === "ResponseCode"
                                       ? "2xx, 4xx, 5xx"
                                       : " "

                  }
                  placeholder={f.placeholder || ""}
                  onChange={(e) => {
                     let inputValue = e.target.value;


                     if (isPortField || isWeightField || isDurationField) {
                        inputValue = inputValue.replace(/\D/g, "");
                     }



                     if (f.fieldName === "RedirectFullURL") {
                        inputValue = inputValue.trim();

                        //  Auto-fix missing : after http/https
                        if (inputValue.startsWith("https//")) {
                           inputValue = inputValue.replace("https//", "https://");
                        }

                        if (inputValue.startsWith("http//")) {
                           inputValue = inputValue.replace("http//", "http://");
                        }
                     }



                     if (f.fieldName === "ResponseCode") {
                        inputValue = inputValue.replace(/\D/g, ""); // remove alphabets

                        // 1. Check the first digit. If it's not 2, 4, or 5, reject the input entirely.
                        if (inputValue.length > 0 && !['2', '4', '5'].includes(inputValue[0])) {
                           return;
                        }

                        // 2. Limit the length to exactly 3 digits maximum.
                        if (inputValue.length > 3) {
                           inputValue = inputValue.slice(0, 3);
                        }
                     }





                     if (isPortField) {
                        inputValue = inputValue.replace(/\D/g, "");
                        //  inputValue = inputValue.replace(/^0+/, "");

                        if (inputValue.length > 1 && inputValue.startsWith("00")) {
                           return;
                        }

                        // Convert 04 -> 4, 05 -> 5, etc.
                        if (inputValue.length > 1 && inputValue.startsWith("0")) {
                           inputValue = String(Number(inputValue));
                        }

                        if (inputValue !== "" && Number(inputValue) > 65535) {
                           inputValue = "65535";
                        }
                     }

                     if (isWeightField) {
                        inputValue = inputValue.replace(/\D/g, "");
                        // If user enters 0, make it 1

                        if (inputValue === "0") {

                           inputValue = "1";

                        }
                        // Allow only single zero
                        if (inputValue.length > 1 && inputValue.startsWith("00")) {
                           return;
                        }

                        // Convert 01 -> 1, 09 -> 9
                        if (inputValue.length > 1 && inputValue.startsWith("0")) {
                           inputValue = String(Number(inputValue));
                        }

                        if (inputValue !== "" && Number(inputValue) > 999) {
                           inputValue = "999";
                        }
                     }

                     if (isDurationField) {
                        inputValue = inputValue.replace(/\D/g, "");

                        // Allow only single zero
                        if (inputValue.length > 1 && inputValue.startsWith("00")) {
                           return;
                        }

                        // Convert 01 -> 1, 09 -> 9
                        if (inputValue.length > 1 && inputValue.startsWith("0")) {
                           inputValue = String(Number(inputValue));
                        }

                        if (inputValue !== "" && Number(inputValue) > 604800) {
                           inputValue = "604800";
                        }
                     }


                     if (isDurationField) {
                        inputValue = inputValue.replace(/\D/g, "");

                        // If user enters 0, make it 1
                        if (inputValue === "0") {
                           inputValue = "1";
                        }

                        // Remove leading zeroes (01 -> 1, 05 -> 5)
                        if (inputValue.length > 1 && inputValue.startsWith("0")) {
                           inputValue = String(Number(inputValue));
                        }

                        if (inputValue !== "" && Number(inputValue) > 604800) {
                           inputValue = "604800";
                        }
                     }


                     onChangeCallback(f.fieldName, inputValue, inputValue);
                  }}
                  type="text"
                  inputProps={
                     isPortField
                        ? {
                           inputMode: "numeric",
                           pattern: "[0-9]*",
                           maxLength: 5
                        }
                        : isWeightField
                           ? {
                              inputMode: "numeric",
                              pattern: "[0-9]*",
                              maxLength: 3
                           }
                           : isDurationField
                              ? { inputMode: "numeric", pattern: "[0-9]*", maxLength: 6 }
                              : f.fieldName === "ResponseCode"
                                 ? { inputMode: "numeric", pattern: "[0-9]*", maxLength: 3 }
                                 : {}
                  }
               />
            </Box>
         );
      }



      if (type === "textarea") {
         return (
            <Box sx={{ width: "100%" }}>
               <FormTextArea
                  name={f.fieldName}
                  label={hideLabel ? "" : f.fieldLabel}
                  value={value}
                  required={f.isRequired}
                  error={isErrorVisible}
                  // helperText={helperText}


                  helperText={
                     isErrorVisible
                        ? error
                        : f.fieldName === "ResponseBody"
                           ? "1024 character maximum"
                           : ""
                  }



                  placeholder={f.placeholder || ""}

                  // onChange={(e) => onChangeCallback(f.fieldName, e.target.value, e.target.value)}

                  onChange={(e) => {
                     let val = e.target.value;

                     if (f.fieldName === "ResponseBody") {
                        val = val.slice(0, 1024); //  limit to 1024
                     }

                     onChangeCallback(f.fieldName, val, val);
                  }}

                  rows={1}
                  maxLength={1024}
               />

               {f.fieldName === "ResponseBody" && (
                  <Typography sx={{ fontSize: "12px", color: "text.secondary", mt: 0.5 }}>
                     1024 character maximum
                  </Typography>
               )}

            </Box>
         );
      }

      if (type === "dropdown") {
         let rawOptions = [];

      
         if (f.dropdownType === "api") {
            if (listenerIdx !== null && f.fieldName === "TargetGroup") {
               rawOptions = nestedDropdownData[`${listenerIdx}_${f.fieldName}`] || [];
            } else if (
               ["SecurityCategory", "PolicyName", "CertificateFromACM", "WAFWebACL"].includes(f.fieldName)
            ) {
               rawOptions = globalApiDropdownData[f.fieldName] || [];
            } else {
               rawOptions = dropdownData[f.fieldName] || [];
            }
         } else {
            try {
               rawOptions = JSON.parse(f.dropdownValues || "[]");
            } catch {
               rawOptions = [];
            }
         }

         if (f.dropdownType === "api") {
            if (listenerIdx !== null && f.fieldName === "TargetGroup") {
               rawOptions = nestedDropdownData[`${listenerIdx}_${f.fieldName}`] || [];
            } else if (
               ["SecurityCategory", "PolicyName", "CertificateFromACM", "WAFWebACL"].includes(f.fieldName)
            ) {
               rawOptions = globalApiDropdownData[f.fieldName] || [];
            } else {
               rawOptions = dropdownData[f.fieldName] || [];
            }
         } else {
            try {
               rawOptions = JSON.parse(f.dropdownValues || "[]");
            } catch {
               rawOptions = [];
            }
         }
         const options = normalizeOptions(rawOptions);


         

         return (
            <Box sx={{ width: "100%", mt: 1 }}>
               <Autocomplete
                  options={options}
                  value={options.find((o) => String(o.value) === String(value)) || null}
                  getOptionLabel={(option) => {
                     if (option == null) return "";
                     if (typeof option === "string") return option;
                     return option.label ?? option.text ?? option.value ?? "";
                  }}
                  isOptionEqualToValue={(opt, val) => String(opt.value) === String(val?.value)}
             
      getOptionDisabled={(option) => f.fieldName === "TargetGroup" && (listenerContext?.TargetGroupCollection?.value || []).some(r => r !== contextData && String(r?.TargetGroup?.value) === String(option.value))}

                  onChange={(e, newValue) => {
                     onChangeCallback(
                        f.fieldName,
                        newValue?.value || "",
                        newValue?.label || newValue?.text || ""
                     );
                  }}
                  renderInput={(params) => (
                     <TextField
                        {...params}
                        label={hideLabel ? "" : f.fieldLabel}
                        required={f.isRequired}
                        size="medium"
                        error={isErrorVisible}
                        helperText={helperText || ""}
                     />
                  )}
               />
            </Box>
         );
      }

      if (type === "radio button") {
         let rawOptions = [];
         if (f.dropdownValues) {
            try { rawOptions = JSON.parse(f.dropdownValues); } catch (e) { }
         }
         if (!Array.isArray(rawOptions) || rawOptions.length === 0) {
            rawOptions = f.dropdownSource ? f.dropdownSource.split(",") : [];
         }

         const options = normalizeOptions(rawOptions);

         const isWafRadio = f.fieldName === "WAF";
         const isCertRadio = f.fieldName === "CertificateSource";
         const isInlineRadio = isWafRadio || isCertRadio;
         const shouldHideRadioLabel = hideLabel || isInlineRadio;
         const boxMarginTop = isWafRadio ? "9px" : "9px";

         let paddingTop = 0;
         if (isCertRadio) paddingTop = 3.5;
         if (isWafRadio) paddingTop = 0.5;

         return (
            <Box sx={{ width: "100%", mt: boxMarginTop }}>
               <FormRadio
                  name={f.fieldName}
                  label={shouldHideRadioLabel ? "" : f.fieldLabel}
                  options={options}
                  value={value}
                  required={f.isRequired}
                  error={isErrorVisible}
                  helperText={helperText}
                  onChange={(e) => onChangeCallback(f.fieldName, e.target.value, e.target.value)}
                  sx={{
                     mt: 0,
                     ml: 0,
                     '& > .MuiTypography-root': {
                        fontWeight: 700,
                        mb: 0.5,
                        display: shouldHideRadioLabel ? 'none' : 'block'
                     }
                  }}
               />
            </Box>
         );
      }

      if (type === "checkbox") {
         const checked = value === true || String(value).toLowerCase() === "true";
         return (
            <Box sx={{ width: "100%", mt: 1 }}>
               <FormCheckbox
                  name={f.fieldName}
                  label={hideLabel ? "" : f.fieldLabel}
                  value={checked}
                  required={f.isRequired}
                  error={isErrorVisible}
                  helperText={helperText}
                  onChange={(e) => onChangeCallback(f.fieldName, e.target.checked, String(e.target.checked))}
               />
            </Box>
         );
      }

      return null;
   };

   const renderNestedCollection = (listenerIdx, collectionField, item) => {
      if (!shouldShowField(collectionField, item)) return null;

      const nestedFields = collectionField.collectionFields || [];
      const rows = Array.isArray(item?.[collectionField.fieldName]?.value) ? item[collectionField.fieldName].value : [];
      const maxCount = collectionField.maxCount || 5;
      const isTargetGroup = collectionField.fieldName === "TargetGroupCollection";

      return (
         <Box sx={{ width: "100%", pt: 2 }}>
            <Typography sx={{ fontSize: "14px", fontWeight: 700, mb: 0.5 }}>
               Forward to target group
            </Typography>
            <Typography sx={{ fontSize: "13px", color: "text.secondary", mb: 2 }}>
               Choose a target group and specify routing weight
            </Typography>

            {(rows || []).filter(row => row).map((row, nestedIdx) => {
               if (isTargetGroup) {
                  const tgDropdownField = (nestedFields || []).find(f => f?.fieldName?.includes("TargetGroup"));
                  const weightField = (nestedFields || []).find(f => f?.fieldName === "Weight");

                  return (
                     <Box key={nestedIdx} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "4fr 1.5fr 80px 40px" }, rowGap: 1, columnGap: 2, mb: 1, alignItems: "start" }}>
                        <Box>
                           {/* {tgDropdownField && renderGenericField(tgDropdownField, row, item, (name, val, lbl) => updateNestedChild(listenerIdx, collectionField.fieldName, nestedIdx, name, val, lbl), false, listenerIdx)}
 */}

                           {tgDropdownField && renderGenericField(
                              tgDropdownField,
                              row,
                              item,
                              (name, val, lbl) =>
                                 updateNestedChild(listenerIdx, collectionField.fieldName, nestedIdx, name, val, lbl),
                              false,
                              listenerIdx,
                              `listener_${listenerIdx}_${collectionField.fieldName}_${nestedIdx}_${tgDropdownField.fieldName}`
                           )}
                        </Box>
                        <Box>
                           {/* {weightField && renderGenericField(weightField, row, item, (name, val, lbl) => updateNestedChild(listenerIdx, collectionField.fieldName, nestedIdx, name, val, lbl), false, listenerIdx)} */}
                           {weightField && renderGenericField(
                              weightField,
                              row,
                              item,
                              (name, val, lbl) =>
                                 updateNestedChild(listenerIdx, collectionField.fieldName, nestedIdx, name, val, lbl),
                              false,
                              listenerIdx,
                              `listener_${listenerIdx}_${collectionField.fieldName}_${nestedIdx}_${weightField.fieldName}`
                           )}

                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", mt: 0.5 }}>
                           <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "primary.main", mb: 0.5 }}>
                              Percent
                           </Typography>
                           <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                              {calculatePercent(listenerIdx, collectionField.fieldName, nestedIdx)}
                           </Typography>
                        </Box>
                        {rows.length > 1 && (
                           <IconButton color="error" sx={{ mt: 0.5 }} onClick={() => removeNestedRow(listenerIdx, collectionField.fieldName, nestedIdx)}>
                              <RemoveCircleOutline />
                           </IconButton>)}





                        {/*  INSERT HERE (INSIDE SAME BOX) */}



                     </Box>
                  )
               }

               return (
                  <Box key={nestedIdx} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, rowGap: 1.5, columnGap: 3, mb: 1, pb: 2, borderBottom: "1px dashed #ccc" }}>
                     {(nestedFields || []).filter(nf =>
                        nf &&

                        nf.isDisplay !== false &&            //  ADD
                        shouldShowField(nf, row, item)       //  ADD
                     ).map(nf => {
                        const isFullRow = shouldFieldBeFullRow(nf);
                        return (
                           <Box key={nf?.fieldId} sx={{ gridColumn: isFullRow ? "1 / -1" : "auto" }}>
                              {/* {renderGenericField(nf, row, item, (name, val, lbl) => updateNestedChild(listenerIdx, collectionField.fieldName, nestedIdx, name, val, lbl))} */}
                              {renderGenericField(
                                 nf,
                                 row,
                                 item,
                                 (name, val, lbl) =>
                                    updateNestedChild(listenerIdx, collectionField.fieldName, nestedIdx, name, val, lbl),
                                 false,
                                 listenerIdx,
                                 `listener_${listenerIdx}_${collectionField.fieldName}_${nestedIdx}_${nf.fieldName}`
                              )}

                           </Box>
                        );
                     })}
                     {rows.length > 1 && (
                        <IconButton color="error" onClick={() => removeNestedRow(listenerIdx, collectionField.fieldName, nestedIdx)} sx={{ gridColumn: "1 / -1", justifySelf: "start" }}>
                           <RemoveCircleOutline />
                        </IconButton>
                     )}
                  </Box>
               )
            })}

            {rows.length < maxCount && (
               <Button startIcon={<AddOutlined />} onClick={() => addNestedRow(listenerIdx, collectionField.fieldName, nestedFields)} sx={{ textTransform: "none", fontWeight: 600 }}>
                  Add {collectionField.fieldLabel}
               </Button>
            )}


            {/*  render ONCE per listener (not inside row loop) */}
            {listenerIdx === expandedIndex && (

               <Box sx={{ mt: 3 }}>

                  <Typography sx={{ fontSize: "15px", fontWeight: 700 }}>
                     Target group stickiness
                  </Typography>

                  <Typography sx={{ fontSize: "13px", color: "text.secondary", mb: 1 }}>
                     Enables the load balancer to bind a user's session to a specific target group
                  </Typography>

                  {collectionFields
                     .filter(f => f.subTab === "Target group stickiness")
                     .map(f => (
                        <Box key={f.fieldId} sx={{ mt: 1 }}>
                           {renderGenericField(
                              f,
                              item,
                              null,
                              (name, val, lbl) =>
                                 updateChild(listenerIdx, name, val, lbl),
                              false,
                              listenerIdx
                           )}
                        </Box>
                     ))}
               </Box>

            )}
         </Box>
      );
   };

   const sectionTitle = field.subTab || field.fieldLabel || "Item";

   return (
      <Box sx={{ width: "100%", pb: 5 }}>
         {!!validationErrors[field.fieldName] && (showValidationErrors || formSubmitted) && (
            <Typography color="error" sx={{ mb: 1, fontSize: "0.75rem" }}>
               {validationErrors[field.fieldName]}
            </Typography>
         )}
         {/* ==========================================
          SECTION 1: INNER LISTENER CARDS
      ========================================== */}
         {(items || []).filter(item => item).map((item, idx) => (
            <Accordion
               key={idx}
               expanded={expandedIndex === idx}
               onChange={(_, isExpanded) => setExpandedIndex(isExpanded ? idx : -1)}
               disableGutters
               sx={{ mb: 2, borderRadius: "8px", border: "1px solid", borderColor: "divider", "&::before": { display: "none" } }}
            >
               <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: "flex", gap: 2 }}>
                     <Typography sx={{ fontWeight: 600 }}>{sectionTitle} {idx + 1}</Typography>
                     <Typography sx={{ fontWeight: 400 }}>
                        {item?.Protocol?.value}  {item?.Port?.value}
                     </Typography>
                  </Box>

                  {items.length > 1 && (
                     <Box
                        component="div"
                        onClick={(e) => { e.stopPropagation(); removeListener(idx); }}
                        sx={{
                           ml: "auto",
                           cursor: "pointer",
                           color: "error.main",
                           fontSize: "0.875rem",
                           fontWeight: 500,
                           padding: "4px 8px",
                           borderRadius: "4px",
                           transition: "background-color 0.2s",
                           "&:hover": { backgroundColor: "error.lighter" }
                        }}
                     >
                        Remove
                     </Box>
                  )}
               </AccordionSummary>
               <AccordionDetails sx={{ px: 3, pt: 0, pb: 3 }}>

                  {/* Because innerSubTabs is now explicitly sorted, the visual order inside the Accordion will follow your rule */}
                  {(innerSubTabs || []).filter(tab => tab).map((subTab, tabIndex) => {
                     const fieldsInSubTab = (collectionFields || []).filter(f => (f?.subTab || "General") === subTab
                        &&
                        f.subTab !== "Target group stickiness"
                     );
                     const hasVisibleFields = (fieldsInSubTab || []).some(f => shouldShowField(f, item));
                     if (!hasVisibleFields) return null;

                     return (
                        <Box key={subTab} sx={{ mb: tabIndex === innerSubTabs.length - 1 ? 0 : 4 }}>
                           <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, rowGap: 1.5, columnGap: 3, alignItems: "start" }}>
                              {(fieldsInSubTab || [])
                                 .filter(f => f && f.isDisplay !== false && shouldShowField(f, item)) //  FIX: Filter hidden fields BEFORE creating grid boxes
                                 .map(f => {
                                    if (f?.fieldType?.toLowerCase().trim() === "collection") {
                                       return <Box key={f?.fieldId} sx={{ gridColumn: "1 / -1" }}>{renderNestedCollection(idx, f, item)}</Box>;
                                    }

                                    const isFullRow = shouldFieldBeFullRow(f);

                                    return (
                                       <Box key={f.fieldId} sx={{ gridColumn: isFullRow ? "1 / -1" : "auto" }}>
                                          {renderGenericField(f, item, null, (name, val, lbl) => updateChild(idx, name, val, lbl), false, idx)}
                                       </Box>
                                    );
                                 })}
                           </Box>
                        </Box>
                     );
                  })}

               </AccordionDetails>
            </Accordion>
         ))}

         <Box sx={{ display: "flex", justifyContent: "flex-start", pb: 1.5, mb: 3 }}>
            <Button variant="outlined" startIcon={<AddOutlined />} onClick={addListener} sx={{ textTransform: "none" }}>
               Add {sectionTitle}
            </Button>
         </Box>

         {(globalSubTabs || []).length > 0 && <Divider sx={{ mb: 3, mt: 1 }} />}
         {(() => {
            return globalSubTabs.map((subTabName, idx) => {
               const isSecurityTab = subTabName.toLowerCase().includes("secure");

               if (isSecurityTab && !hasHttpsListener) return null;

               const subTabFields = (extraRootFields || []).filter(f => f?.subTab === subTabName);
               const visibleFields = (subTabFields || []).filter(f => f.isDisplay !== false && shouldShowField(f, globalContextData));

               if (visibleFields.length === 0) return null;

               return (
                  <Box key={subTabName} sx={{ mb: 4, mt: idx === 0 ? 2 : 0 }}>

                     <Typography
                        variant="h6"
                        sx={{
                           fontWeight: 700,
                           mb: 0,
                        }}
                     >
                        {subTabName}
                     </Typography>

                     <Box sx={{
                        display: "grid",
                        gridTemplateColumns: {
                           xs: "1fr",
                           md: isSecurityTab ? "1fr 1fr 1fr" : "1fr 1fr"
                        },
                        rowGap: 1.5,
                        columnGap: 3,
                        alignItems: "start"
                     }}>
                        {(visibleFields || []).filter(f => f).map(f => {
                           const isFullRow = shouldFieldBeFullRow(f);

                           return (
                              <Box key={f?.fieldId} sx={{ gridColumn: isFullRow ? "1 / -1" : "auto" }}>
                                 {renderGenericField(f, globalContextData, null, (name, val, lbl) => updateGlobalField(name, val, lbl))}
                              </Box>
                           );
                        })}
                     </Box>
                  </Box>
               );
            });
         })()}

      </Box>
   );
});

ListenersAlbAccordion.displayName = 'ListenersAlbAccordion';

export default ListenersAlbAccordion;
